import { useCallback, useState } from "react";
import { BACKOFFICE_SERVICE } from "../../../core/services/backoffice-service";
import { HELPER } from "../../helper/helper";
import { CustomMessage } from "../alert/custom-message";
import { CustomToast } from "../alert/custom-toast";
import { CustomLoader } from "../custom-loader/custom-loader";
import { FormInput } from "../form-component/form-input";
import { Optional } from "../optional/optional";

const CANCEL_SUCCESS_MESSAGES = {
  APPROVED: {
    force: "Mandate force cancelled successfully!",
    cancel: "Mandate cancelled successfully!",
  },
  PENDING: {
    force: "Mandate force cancellation request has been submitted for approval.",
    cancel: "Mandate cancellation request has been submitted for approval.",
  },
  default: "Mandate cancelled successfully!",
};

export function CancelMandateConfirmation(props) {
  const isForceCancel = props.cancellationType === "force";

  const [confirmationText, setConfirmationText] = useState(null);
  const [reason, setReason] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [viewIndex, setViewIndex] = useState(0);
  const [afterData, setAfterData] = useState(null);
  const [requestStatus, setRequestStatus] = useState(null);

  const handleSubmit = useCallback(() => {
    setLoading(true);
    setError(null);

    const payload = {
      actionType: isForceCancel ? "MANDATE_FORCE_CANCEL" : "MANDATE_CANCEL",
      mandateReference: props.mandateReference,
      reason,
    };

    BACKOFFICE_SERVICE.INITIATE_MAKER_CHECKER_REQUEST(payload)
      .then((data) => {
        const result = data?.result;
        setAfterData(result?.afterData ?? null);
        setRequestStatus(result?.requestStatus);
        setViewIndex(1);
        setLoading(false);
      })
      .catch((err) => {
        setError(
          err?.status === 403
            ? "You do not have permission to initiate this request"
            : HELPER.PROCESS_ERROR(err)
        );
        setLoading(false);
      });
  }, [reason, props.mandateReference, isForceCancel]);

  const isSubmitDisabled =
    confirmationText !== props.mandateReference ||
    !reason ||
    /^\s*$/.test(reason);

  const cancelType = isForceCancel ? "force" : "cancel";
  
  const successMessage =
    CANCEL_SUCCESS_MESSAGES[requestStatus]?.[cancelType] ?? CANCEL_SUCCESS_MESSAGES.default;

  const renderForm = () => (
    <div>
      <div className="p-pb-1">
        <p className="confirm-text p-mt-0">
          Confirm that you want to{isForceCancel ? " force cancel" : " cancel"} this
          mandate.
        </p>
      </div>
      <div>
        <div className="p-grid">
          <div className="p-col-12">
            <label>Reason:</label>
            <FormInput
              value={reason}
              type="INPUT"
              fn={(e) => setReason(e?.target?.value)}
              placeholder=""
            />
          </div>
          <div className="p-col-12">
            <label className="p-mt-2">
              To confirm, enter the reference
              <b>, {props.mandateReference},</b> in the input field below:
            </label>
            <FormInput
              value={confirmationText}
              type="INPUT"
              fn={(e) => setConfirmationText(e?.target?.value)}
              placeholder="Reference"
            />
          </div>
        </div>
      </div>
      <Optional show={error}>
        <CustomToast title="Error" description={error} type="error" />
      </Optional>
      <Optional show={!loading}>
        <div className="p-mt-5 p-pb-4">
          <div className="p-grid p-mt-4">
            <div className={loading ? "p-col-12" : "p-col-6"}>
              <button onClick={props.closeModal} className="secondary-button">
                Cancel
              </button>
            </div>
            <div className={loading ? "p-col-12" : "p-col-6"}>
              <button
                disabled={isSubmitDisabled}
                onClick={handleSubmit}
                className="primary-button"
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      </Optional>
      <Optional show={loading}>
        <div className="p-my-3">
          <CustomLoader loadingText="Loading..." />
        </div>
      </Optional>
    </div>
  );

  const renderSuccess = () => (
    <div className="p-mt-2 p-pb-1">
      <CustomMessage messageType="success" message={successMessage} />
      <div className="p-mt-2 p-pb-1">
        <p
          onClick={() => props.closeModal({ reload: true, afterData })}
          className="close-modal add-cursor"
        >
          Close
        </p>
      </div>
    </div>
  );

  return <div>{viewIndex === 0 ? renderForm() : renderSuccess()}</div>;
}
